import axios from "axios";
import { baseURL } from "../utils/constants";
import {
  CLEAR_LOADING,
  CLEAR_USER,
  SET_ALERT,
  SET_LOADING,
  SET_USER,
} from "../context/Action";
import MD5 from "crypto-js/md5";
// Add an interceptor to handle token expiration
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("authUser");
      delete axios.defaults.headers.common["Authorization"];
    }
    return Promise.reject(error);
  }
);

export const handleSignIn = async (dispatch, { email, password }) => {
  try {
    dispatch({ type: SET_LOADING, payload: true });
    const response = await axios.post(`${baseURL}/users/login`, {
      email,
      password,
    });
    const token = response.data?.data?.token;
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("authUser", JSON.stringify(response.data?.data));
      dispatch({ type: SET_USER, payload: response.data?.data });
      return response.data?.data;
    } else if (response?.data?.data?.tempToken) {
      localStorage.setItem(
        "tempToken",
        JSON.stringify(response.data?.data?.tempToken)
      );
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: response.data?.data?.message,
          alertType: "info",
        },
      });
      return response.data?.data;
    } else {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText:
            "Invalid email or password. Please enter valid credentials.",
          alertType: "error",
        },
      });
    }
  } catch (error) {
    if (error.response) {
      // If the server responds with an error, check the error status and display appropriate messages
      if (error.response.status === 400) {
        dispatch({
          type: SET_ALERT,
          payload: {
            alertText: "Invalid password. Please enter a valid password.",
            alertType: "error",
          },
        });
      } else if (error.response.status === 401) {
        dispatch({
          type: SET_ALERT,
          payload: {
            alertText:
              "Invalid email format. Please enter a valid email address.",
            alertType: "error",
          },
        });
      } else {
        dispatch({
          type: SET_ALERT,
          payload: {
            alertText:
              error.response?.data?.data?.message ||
              "An error occurred while logging in.",
            alertType: "error",
          },
        });
      }
    } else {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: "An error occurred while logging in.",
          alertType: "error",
        },
      });
    }
  } finally {
    dispatch({ type: CLEAR_LOADING });
  }
};

export const handleSignOut = (dispatch) => {
  localStorage.removeItem("authUser");
  axios.defaults.headers.common["Authorization"] = null;
  dispatch({ type: CLEAR_USER });
  dispatch({
    type: SET_ALERT,
    payload: {
      alertText: "You have been successfully logged out.",
      alertType: "info",
    },
  });
};

export const handleEnableMFA = async (dispatch) => {
  try {
    dispatch({ type: SET_LOADING, payload: true });
    const response = await axios.post(`${baseURL}/users/enable-mfa`);
    const data = response.data?.data;
    if (data) {
      return data;
    }
    throw new Error("An error occurred while enabling MFA.");
  } catch (error) {
    if (error.response) {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText:
            error.response?.data?.data?.message ||
            "An error occurred while enabling MFA.",
          alertType: "error",
        },
      });
    }
  } finally {
    dispatch({ type: CLEAR_LOADING });
  }
};

export const handleVerifyMFA = async (dispatch, otp) => {
  try {
    dispatch({ type: SET_LOADING, payload: true });
    const response = await axios.post(`${baseURL}/users/verify-mfa`, otp);
    if (response.data?.status === 200) {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: "MFA has been successfully enabled.",
          alertType: "info",
        },
      });
    }
    return response.data?.data;
  } catch (error) {
    dispatch({
      type: SET_ALERT,
      payload: {
        alertText:
          error.response?.data?.data?.message ||
          "An error occurred while verifying MFA.",
        alertType: "error",
      },
    });
    return false;
  } finally {
    dispatch({ type: CLEAR_LOADING });
  }
};

export const handleCancelMFA = async (dispatch) => {
  try {
    dispatch({ type: SET_LOADING, payload: true });
    const response = await axios.post(`${baseURL}/users/cancel-mfa`);
    if (response.data?.status === 200) {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: response.data?.data.message,
          alertType: "info",
        },
      });
    }
  } catch (error) {
    dispatch({
      type: SET_ALERT,
      payload: {
        alertText:
          error.response?.data?.data?.message ||
          "An error occurred during MFA setup.",
        alertType: "error",
      },
    });
  } finally {
    dispatch({ type: CLEAR_LOADING });
  }
};

export const handleDisableMFA = async (dispatch) => {
  try {
    dispatch({ type: SET_LOADING, payload: true });
    const response = await axios.post(`${baseURL}/users/disable-mfa`);
    if (response.data?.status === 200) {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: "MFA disabled successfully.",
          alertType: "info",
        },
      });
    }
    const data = response.data?.data;
    if (data) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
      localStorage.setItem("authUser", JSON.stringify(data));
      dispatch({ type: SET_USER, payload: data });
    } else {
      throw new Error("Invalid token. Please try again.");
    }
    return true;
  } catch (error) {
    dispatch({
      type: SET_ALERT,
      payload: {
        alertText:
          error.response?.data?.data?.message ||
          "An error occurred while disabling MFA.",
        alertType: "error",
      },
    });
    return false;
  } finally {
    dispatch({ type: CLEAR_LOADING });
  }
};

export const handleForgotMFA = async (dispatch, backupCode) => {
  try {
    dispatch({ type: SET_LOADING, payload: true });
    const response = await axios.post(`${baseURL}/users/forgot-mfa`, {
      backupCode: MD5(backupCode).toString(),
      tempToken: JSON.parse(localStorage.getItem("tempToken")),
    });
    if (response.data?.status === 200) {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: response.data?.data.message,
          alertType: "info",
        },
      });
    }
    return true;
  } catch (error) {
    dispatch({
      type: SET_ALERT,
      payload: {
        alertText:
          error.response?.data?.data?.message ||
          "An error occurred while disabling MFA.",
        alertType: "error",
      },
    });
    return false;
  } finally {
    dispatch({ type: CLEAR_LOADING });
  }
};

export const verifyLoginOTP = async (dispatch, otp) => {
  try {
    dispatch({ type: SET_LOADING, payload: true });
    const response = await axios.post(`${baseURL}/users/verify-otp`, {
      otp: otp,
      tempToken: JSON.parse(localStorage.getItem("tempToken")),
    });
    const token = response.data?.data?.token;
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("authUser", JSON.stringify(response.data?.data));
      dispatch({ type: SET_USER, payload: response.data?.data });
      localStorage.removeItem("tempToken");
      return response.data?.data;
    } else {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: "Invalid OTP. Please enter a valid OTP.",
          alertType: "error",
        },
      });
    }
  } catch (error) {
    if (error.response && error?.response?.data?.status === 400) {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: "Invalid OTP. Please enter a valid OTP.",
          alertType: "error",
        },
      });
      return false;
    } else {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText:
            error.response?.data?.data?.message ||
            "An error occurred while verifying the OTP.",
          alertType: "error",
        },
      });
      localStorage.removeItem("tempToken");
      delete axios.defaults.headers.common["Authorization"];
      return null;
    }
  } finally {
    dispatch({ type: CLEAR_LOADING });
  }
};

export const handleSignUp = async (
  dispatch,
  { firstName, lastName, organization, mobile, email, password }
) => {
  try {
    dispatch({ type: SET_LOADING, payload: true });
    const response = await axios.post(`${baseURL}/users/signup`, {
      firstname: firstName,
      lastname: lastName,
      email,
      mobile,
      password,
      organization,
    });
    const token = response.data?.data?.token;
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("authUser", JSON.stringify(response.data?.data));
      dispatch({ type: SET_USER, payload: response.data?.data });
    } else {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText:
            "Invalid email or password. Please enter valid credentials.",
          alertType: "error",
        },
      });
    }
  } catch (error) {
    dispatch({
      type: SET_ALERT,
      payload: {
        alertText:
          error.response?.data?.data?.message ||
          "An error occurred while signing in.",
        alertType: "error",
      },
    });
  } finally {
    dispatch({ type: CLEAR_LOADING });
  }
};

export const handleForgotPassword = async (dispatch, { email }) => {
  try {
    dispatch({ type: SET_LOADING, payload: true });
    const response = await axios.put(`${baseURL}/users/forgot-password`, {
      email,
    });
    if (response.data?.status === 200) {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: "Password reset link has been sent to your email.",
          alertType: "info",
        },
      });
    } else {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: "An error occurred while sending the password reset link.",
          alertType: "error",
        },
      });
    }
  } catch (error) {
    dispatch({
      type: SET_ALERT,
      payload: {
        alertText:
          error.response?.data?.data?.message ||
          "An error occurred while sending the password reset link.",
        alertType: "error",
      },
    });
  } finally {
    dispatch({ type: CLEAR_LOADING });
  }
};

export const handleChangePassword = async (dispatch, { email, password }) => {
  try {
    dispatch({ type: SET_LOADING, payload: true });
    const response = await axios.put(`${baseURL}/users/change-password`, {
      email,
      password,
    });
    if (response.data?.status === 200) {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: "Password has been successfully changed.",
          alertType: "info",
        },
      });
    } else {
      dispatch({
        type: SET_ALERT,
        payload: {
          alertText: "An error occurred while changing the password.",
          alertType: "error",
        },
      });
    }
  } catch (error) {
    dispatch({
      type: SET_ALERT,
      payload: {
        alertText:
          error.response?.data?.data?.message ||
          "An error occurred while changing the password.",
        alertType: "error",
      },
    });
  } finally {
    dispatch({ type: CLEAR_LOADING });
  }
};
